import React, { useState, useEffect } from 'react';
import {
    Box, TextField, Button, Typography, Link, Divider, Paper, Radio, RadioGroup,
    FormControlLabel, FormControl, Checkbox, Alert, CircularProgress, InputAdornment,
    IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip,
    Avatar, LinearProgress
} from '@mui/material';
import {
    Person as PersonIcon, Lock as LockIcon, Email as EmailIcon, Phone as PhoneIcon, CheckCircle as CheckCircleIcon,
    Visibility, VisibilityOff, ArrowBack as ArrowBackIcon, ShieldOutlined as ShieldIcon, HttpsOutlined as HttpsIcon,
    VerifiedOutlined as VerifiedIcon, AutoAwesome as AiIcon, CloudUploadOutlined as UploadIcon,
    PsychologyOutlined as BrainIcon, PersonSearchOutlined as SearchIcon, DescriptionOutlined as DocIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import logoImage from '../Assets/Images/logo.png';
import person1 from '../Assets/Images/person1.jpg';
import person2 from '../Assets/Images/person2.jpg';
import { registerApi, freelancerRegisterApi, sendMobileVerification, confirmMobileVerification, sendEmailVerification, confirmEmailVerification, checkExistsApi } from '../Api/AuthPermissionsApi';

const getPasswordStrength = (password) => {
    if (!password) return { score: 0, label: '', color: '', percent: 0 };
    
    let score = 0;
    const hasMinLength = password.length >= 6;
    const hasStrongLength = password.length >= 8;
    const hasLower = /[a-z]/.test(password);
    const hasUpper = /[A-Z]/.test(password);
    const hasDigit = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);

    if (hasMinLength) score += 1;
    if (hasStrongLength) score += 1;
    
    let varietyCount = 0;
    if (hasLower) varietyCount++;
    if (hasUpper) varietyCount++;
    if (hasDigit) varietyCount++;
    if (hasSpecial) varietyCount++;

    if (varietyCount >= 2) score += 1;
    if (varietyCount >= 4) score += 1;

    if (password.length > 0 && password.length < 6) {
        return { score: 1, label: 'Weak', color: '#EF4444', percent: 25 };
    }
    
    if (score <= 2) {
        return { score: 2, label: 'Weak', color: '#EF4444', percent: 33 };
    } else if (score === 3) {
        return { score: 3, label: 'Medium', color: '#F59E0B', percent: 66 };
    } else {
        return { score: 4, label: 'Strong', color: '#10B981', percent: 100 };
    }
};

const STEPS = [
    { id: 0, label: 'Upload JD', color: '#60A5FA' },
    { id: 1, label: 'AI Analyze', color: '#C084FC' },
    { id: 2, label: 'Scan Talent', color: '#FBBF24' },
    { id: 3, label: 'AI Match', color: '#34D399' },
];

/* ─────────────────────────────────────────────────────
   SLIDE COMPONENTS
───────────────────────────────────────────────────── */

function SlideUpload({ active }) {
    const [phase, setPhase] = useState('idle');

    useEffect(() => {
        if (!active) { setPhase('idle'); return; }
        const t1 = setTimeout(() => setPhase('flying'), 100);
        const t2 = setTimeout(() => setPhase('landed'), 900);
        const t3 = setTimeout(() => setPhase('scanning'), 1500);
        const t4 = setTimeout(() => setPhase('done'), 3200);
        return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
    }, [active]);

    const transforms = {
        idle: { opacity: 0, transform: 'scale(0.1) translateY(-200px) rotate(-10deg)', filter: 'blur(6px)' },
        flying: { opacity: 1, transform: 'scale(0.5) translateY(-60px) rotate(-4deg)', filter: 'blur(1px)' },
        landed: { opacity: 1, transform: 'scale(1)   translateY(0px)   rotate(0deg)', filter: 'blur(0px)' },
        scanning: { opacity: 1, transform: 'scale(1)   translateY(0px)   rotate(0deg)', filter: 'blur(0px)' },
        done: { opacity: 1, transform: 'scale(1)   translateY(0px)   rotate(0deg)', filter: 'blur(0px)' },
    };
    const current = transforms[phase] || transforms.idle;

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <UploadIcon sx={{ color: '#60A5FA', fontSize: 16 }} />
                <Typography sx={{ color: '#60A5FA', fontWeight: 700, fontSize: '11px', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Step 1 · Upload Job Description
                </Typography>
            </Box>

            <Box sx={{
                border: phase === 'done' ? '1.5px solid rgba(52,211,153,0.4)' : '1.5px dashed rgba(96,165,250,0.35)',
                borderRadius: '10px', p: 2.2, mb: 1.5, minHeight: 110,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                backgroundColor: phase === 'done' ? 'rgba(52,211,153,0.04)' : 'rgba(96,165,250,0.04)',
                transition: 'all 0.4s ease', position: 'relative', overflow: 'hidden'
            }}>
                {phase === 'scanning' && (
                    <Box sx={{
                        position: 'absolute', left: 0, right: 0, top: 0, height: '2px',
                        background: 'linear-gradient(90deg, transparent, #60A5FA, transparent)',
                        boxShadow: '0 0 8px #60A5FA',
                        animation: 'scanLine 1.5s ease-in-out forwards'
                    }} />
                )}

                <Box sx={{
                    ...current,
                    transition: 'transform 0.7s cubic-bezier(0.2, 1.4, 0.4, 1), opacity 0.5s ease, filter 0.4s ease',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                    animation: phase === 'done' ? 'floatFile 3s ease-in-out infinite' : 'none'
                }}>
                    <Box sx={{
                        width: 54, height: 64, borderRadius: '8px',
                        background: 'linear-gradient(145deg, #1E3A8A, #2563EB)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        boxShadow: ['landed', 'scanning', 'done'].includes(phase) ? '0 10px 28px rgba(37,99,235,0.5)' : '0 2px 8px rgba(0,0,0,0.3)',
                        transition: 'box-shadow 0.4s ease', position: 'relative', overflow: 'hidden'
                    }}>
                        <Box sx={{
                            position: 'absolute', top: 0, left: '-60%', width: '40%', height: '100%',
                            background: 'linear-gradient(105deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%)',
                            animation: phase === 'done' ? 'shimmer 2.5s linear infinite' : 'none',
                        }} />
                        <DocIcon sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 24, mb: 0.2 }} />
                        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '9.5px', letterSpacing: 1 }}>PDF</Typography>
                        <Box sx={{ position: 'absolute', top: 0, right: 0, borderLeft: '10px solid transparent', borderBottom: '10px solid rgba(0,0,0,0.2)' }} />
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{ color: '#CBD5E1', fontSize: '13px', fontWeight: 600, display: 'block' }}>Senior_FullStack_JD.pdf</Typography>
                        <Typography sx={{ color: '#475569', fontSize: '11px' }}>2.4 MB</Typography>
                    </Box>
                </Box>
            </Box>

            {(phase === 'scanning' || phase === 'done') && (
                <Box sx={{ animation: 'fadeUp 0.3s ease' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography sx={{ color: '#64748B', fontSize: '12px' }}>
                            {phase === 'done' ? '✓ Upload complete' : 'Uploading & parsing…'}
                        </Typography>
                        <Typography sx={{ color: phase === 'done' ? '#34D399' : '#60A5FA', fontWeight: 700, fontSize: '12px' }}>
                            {phase === 'done' ? '100%' : '68%'}
                        </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={phase === 'done' ? 100 : 68} sx={{
                        height: 5, borderRadius: 2.5,
                        backgroundColor: 'rgba(255,255,255,0.08)',
                        '& .MuiLinearProgress-bar': { backgroundColor: phase === 'done' ? '#34D399' : '#60A5FA', transition: 'transform 0.8s ease' }
                    }} />
                </Box>
            )}
        </Box>
    );
}

const AI_TEXT = 'Analyzing requirements for Senior FullStack Engineer…\n\nDetected: 6 core skills, 5+ years experience, remote-friendly role.';

function SlideAnalyze({ active }) {
    const [displayed, setDisplayed] = useState('');
    const [chips, setChips] = useState([]);
    const skills = ['React & Node', '5+ Years', 'AWS/Cloud', 'System Design', 'TypeScript', 'CI/CD'];
    const timerRef = React.useRef(null);

    useEffect(() => {
        if (!active) { setDisplayed(''); setChips([]); return; }
        let idx = 0;
        timerRef.current = setInterval(() => {
            idx++;
            setDisplayed(AI_TEXT.slice(0, idx));
            if (idx >= AI_TEXT.length) {
                clearInterval(timerRef.current);
                skills.forEach((_, i) => setTimeout(() => setChips(p => [...p, i]), i * 50 + 30));
            }
        }, 14);
        return () => clearInterval(timerRef.current);
    }, [active]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <BrainIcon sx={{ color: '#C084FC', fontSize: 16 }} />
                <Typography sx={{ color: '#C084FC', fontWeight: 700, fontSize: '11px', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Step 2 · AI Skill Extraction
                </Typography>
            </Box>

            <Box sx={{
                backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: '8px',
                border: '1px solid rgba(192,132,252,0.18)',
                p: 2, mb: 1.5,
                height: 135,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.2 }}>
                    {[['#F87171', 0.5], ['#FBBF24', 0.5], ['#34D399', 0.5]].map(([c, o], i) => (
                        <Box key={i} sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: c, boxShadow: `0 0 4px ${c}${Math.round(o * 255).toString(16)}` }} />
                    ))}
                    <Typography sx={{ color: '#334155', fontSize: '10px', ml: 0.5, fontFamily: 'monospace' }}>rono-ai ~ analyze</Typography>
                </Box>
                <Typography sx={{
                    color: '#A5F3FC', fontSize: '12.5px', lineHeight: 1.7,
                    whiteSpace: 'pre-wrap', display: 'block',
                    fontFamily: "'Fira Code', 'Courier New', monospace"
                }}>
                    <Box component="span" sx={{ color: '#818CF8' }}>$ </Box>
                    {displayed}
                    {displayed.length < AI_TEXT.length && (
                        <Box component="span" sx={{ animation: 'blink 0.65s step-start infinite', color: '#C084FC' }}>▌</Box>
                    )}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
                {skills.map((s, i) => (
                    <Chip key={i} label={s} size="small" sx={{
                        backgroundColor: 'rgba(192,132,252,0.15)', color: '#E9D5FF',
                        fontWeight: 600, fontSize: '11px', border: '1px solid rgba(192,132,252,0.25)',
                        opacity: chips.includes(i) ? 1 : 0,
                        transform: chips.includes(i) ? 'translateY(0) scale(1)' : 'translateY(6px) scale(0.9)',
                        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
                    }} />
                ))}
            </Box>
        </Box>
    );
}

function SlideScan({ active }) {
    const [count, setCount] = useState(0);
    const [found, setFound] = useState([]);
    const names = ['Rahul S.', 'Priya M.', 'Carlos D.', 'Ayesha K.'];

    useEffect(() => {
        if (!active) { setCount(0); setFound([]); return; }
        const target = 25420;
        const step = Math.ceil(target / 60);
        const t = setInterval(() => {
            setCount(p => {
                if (p + step >= target) { clearInterval(t); return target; }
                return p + step;
            });
        }, 45);
        names.forEach((_, i) => setTimeout(() => setFound(p => [...p, i]), i * 500 + 800));
        return () => clearInterval(t);
    }, [active]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <SearchIcon sx={{ color: '#FBBF24', fontSize: 16 }} />
                <Typography sx={{ color: '#FBBF24', fontWeight: 700, fontSize: '11px', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Step 3 · Talent Pool Scan
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.2, mb: 2 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress size={48} thickness={2.5} variant="determinate" value={100} sx={{ color: 'rgba(255,255,255,0.07)', position: 'absolute' }} />
                    <CircularProgress size={48} thickness={2.5} sx={{ color: '#FBBF24' }} />
                </Box>
                <Box>
                    <Typography sx={{ fontWeight: 800, color: '#F1F5F9', fontSize: '23px', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                        {count.toLocaleString()}
                    </Typography>
                    <Typography sx={{ color: '#64748B', fontSize: '12px' }}>profiles scanned</Typography>
                </Box>
            </Box>

            <LinearProgress variant="indeterminate" sx={{
                height: 3, borderRadius: 2, mb: 1.5,
                backgroundColor: 'rgba(255,255,255,0.07)',
                '& .MuiLinearProgress-bar': { backgroundColor: '#FBBF24' }
            }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.7 }}>
                {names.map((n, i) => (
                    <Chip key={i} label={`✓ ${n}`} size="small" sx={{
                        backgroundColor: 'rgba(251,191,36,0.1)', color: '#FDE68A',
                        fontWeight: 600, fontSize: '11px', border: '1px solid rgba(251,191,36,0.2)',
                        opacity: found.includes(i) ? 1 : 0,
                        transform: found.includes(i) ? 'scale(1)' : 'scale(0.75)',
                        transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)'
                    }} />
                ))}
            </Box>
        </Box>
    );
}

function SlideResults({ active }) {
    const [shown, setShown] = useState([]);
    const candidates = [
        { name: 'Rahul Sharma', role: 'Sr. FullStack · 6 yrs', match: 98, img: person1 },
        { name: 'Priya Mehta', role: 'React Lead · 5 yrs', match: 95, img: person2 },
    ];

    useEffect(() => {
        if (!active) { setShown([]); return; }
        candidates.forEach((_, i) => setTimeout(() => setShown(p => [...p, i]), i * 400 + 150));
    }, [active]);

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                <AiIcon sx={{ color: '#34D399', fontSize: 16 }} />
                <Typography sx={{ color: '#34D399', fontWeight: 700, fontSize: '11px', letterSpacing: 1, textTransform: 'uppercase' }}>
                    Step 4 · AI Ranked Matches
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mb: 1.5 }}>
                {candidates.map((c, i) => (
                    <Box key={i} sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        backgroundColor: i === 0 ? 'rgba(52,211,153,0.07)' : 'rgba(255,255,255,0.04)',
                        border: i === 0 ? '1px solid rgba(52,211,153,0.2)' : '1px solid rgba(255,255,255,0.07)',
                        borderRadius: '9px', p: 1.4, px: 1.6,
                        opacity: shown.includes(i) ? 1 : 0,
                        transform: shown.includes(i) ? 'translateY(0)' : 'translateY(16px)',
                        transition: 'all 0.4s cubic-bezier(0.4,0,0.2,1)'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Avatar src={c.img} sx={{ width: 34, height: 34 }} />
                            <Box>
                                <Typography sx={{ fontWeight: 700, color: '#F1F5F9', fontSize: '13.5px' }}>{c.name}</Typography>
                                <Typography sx={{ color: '#64748B', fontSize: '11px' }}>{c.role}</Typography>
                            </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                            <Typography sx={{ fontWeight: 800, color: '#34D399', fontSize: '17px', lineHeight: 1 }}>{c.match}%</Typography>
                            <Typography sx={{ color: '#64748B', fontSize: '10px' }}>match</Typography>
                        </Box>
                    </Box>
                ))}
            </Box>

            <Box sx={{
                p: 1.4, borderRadius: '8px',
                backgroundColor: 'rgba(52,211,153,0.06)',
                border: '1px solid rgba(52,211,153,0.15)',
                animation: active ? 'fadeUp 0.5s 0.8s both ease' : 'none'
            }}>
                <Typography sx={{ color: '#6EE7B7', fontSize: '12px' }}>
                    🎯 AI shortlisted 2 of 25,420 profiles — 95%+ confidence.
                </Typography>
            </Box>
        </Box>
    );
}

const extractTokenFromResponse = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    for (const [key, value] of Object.entries(payload)) {
        if (typeof value === 'string' && key.toLowerCase().includes('token')) return value;
        if (value && typeof value === 'object') {
            const n = extractTokenFromResponse(value);
            if (n) return n;
        }
    }
    return null;
};

const SignUp = ({ onlyForm }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        let delay = 5500;
        if (activeStep === 0) delay = 5000;
        else if (activeStep === 1) delay = 3800; // Further reduced to 3.8s for maximum snappiness
        else if (activeStep === 2) delay = 5500;
        else if (activeStep === 3) delay = 6500;

        const t = setTimeout(() => {
            setActiveStep(prev => (prev + 1) % 4);
        }, delay);
        return () => clearTimeout(t);
    }, [activeStep]);
    const [recruiterType, setRecruiterType] = useState(() => {
        return sessionStorage.getItem('signup_recruiterType') || 'employer';
    });
    const [formData, setFormData] = useState(() => {
        try {
            const saved = sessionStorage.getItem('signup_formData');
            return saved ? JSON.parse(saved) : {
                email: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                password: '',
                confirmPassword: '',
            };
        } catch (e) {
            return {
                email: '',
                firstName: '',
                lastName: '',
                phoneNumber: '',
                password: '',
                confirmPassword: '',
            };
        }
    });
    const [agreedToTerms, setAgreedToTerms] = useState(() => {
        return sessionStorage.getItem('signup_agreedToTerms') === 'true';
    });
    const [loadingAction, setLoadingAction] = useState('');
    const [error, setError] = useState('');
    const [errors, setErrors] = useState({}); 
    const [successDialogOpen, setSuccessDialogOpen] = useState(false); // Success dialog state
    
    // Email Verification State
    const [emailStatus, setEmailStatus] = useState(() => {
        return sessionStorage.getItem('signup_emailStatus') || 'idle';
    }); // 'idle', 'sent', 'verified'
    const [emailOtp, setEmailOtp] = useState('');
    const [resendTimerEmail, setResendTimerEmail] = useState(0);

    // Mobile Verification State
    const [mobileStatus, setMobileStatus] = useState(() => {
        return sessionStorage.getItem('signup_mobileStatus') || 'idle';
    }); // 'idle', 'sent', 'verified'
    const [mobileOtp, setMobileOtp] = useState('');
    const [resendTimerMobile, setResendTimerMobile] = useState(0);

    const [success, setSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Countdown timers
    useEffect(() => {
        let interval;
        if (resendTimerEmail > 0) {
            interval = setInterval(() => setResendTimerEmail(prev => prev - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimerEmail]);

    useEffect(() => {
        let interval;
        if (resendTimerMobile > 0) {
            interval = setInterval(() => setResendTimerMobile(prev => prev - 1), 1000);
        }
        return () => clearInterval(interval);
    }, [resendTimerMobile]);

    // Save signup details to sessionStorage to survive navigation
    useEffect(() => {
        sessionStorage.setItem('signup_recruiterType', recruiterType);
    }, [recruiterType]);

    useEffect(() => {
        sessionStorage.setItem('signup_formData', JSON.stringify(formData));
    }, [formData]);

    useEffect(() => {
        sessionStorage.setItem('signup_agreedToTerms', agreedToTerms);
    }, [agreedToTerms]);

    useEffect(() => {
        sessionStorage.setItem('signup_emailStatus', emailStatus);
    }, [emailStatus]);

    useEffect(() => {
        sessionStorage.setItem('signup_mobileStatus', mobileStatus);
    }, [mobileStatus]);

    const clearSavedSignupState = () => {
        sessionStorage.removeItem('signup_recruiterType');
        sessionStorage.removeItem('signup_formData');
        sessionStorage.removeItem('signup_agreedToTerms');
        sessionStorage.removeItem('signup_emailStatus');
        sessionStorage.removeItem('signup_mobileStatus');
    };

    // Debounced check for Email existence
    useEffect(() => {
        const email = formData.email.trim();
        if (!email || !email.includes('@') || !email.includes('.')) {
            return;
        }

        const delayDebounceFn = setTimeout(async () => {
            try {
                const res = await checkExistsApi({ email });
                if (res.exists) {
                    setErrors(prev => ({ ...prev, email: res.message || 'Email already exists.' }));
                } else {
                    setErrors(prev => ({ ...prev, email: '' }));
                }
            } catch (err) {
                console.error('Error checking email existence:', err);
            }
        }, 500); // 500ms debounce delay

        return () => clearTimeout(delayDebounceFn);
    }, [formData.email]);

    // Debounced check for Mobile existence
    useEffect(() => {
        const mobile = formData.phoneNumber.trim();
        if (mobile.length !== 10) {
            return;
        }

        const delayDebounceFn = setTimeout(async () => {
            try {
                const fullMobile = '+91' + mobile;
                const res = await checkExistsApi({ mobile: fullMobile });
                if (res.exists) {
                    setErrors(prev => ({ ...prev, phoneNumber: res.message || 'Mobile number already exists.' }));
                } else {
                    setErrors(prev => ({ ...prev, phoneNumber: '' }));
                }
            } catch (err) {
                console.error('Error checking mobile existence:', err);
            }
        }, 500); // 500ms debounce delay

        return () => clearTimeout(delayDebounceFn);
    }, [formData.phoneNumber]);

    const handleInputChange = (field) => (e) => {
        setFormData((prev) => ({
            ...prev,
            [field]: e.target.value,
        }));
        setError(''); // Clear error when user starts typing
        setErrors((prev) => ({ ...prev, [field]: '' }));
        
        // Reset email status if user edits email after sending OTP
        if (field === 'email' && emailStatus === 'sent') {
            setEmailStatus('idle');
            setEmailOtp('');
        }
    };

    const handlePhoneNumberChange = (e) => {
        // Only allow numbers and limit to 10 digits
        const value = e.target.value.replace(/\D/g, '').slice(0, 10);
        setFormData((prev) => ({
            ...prev,
            phoneNumber: value,
        }));
        setError(''); // Clear error when user starts typing
        setErrors((prev) => ({ ...prev, phoneNumber: '' }));
        
        // Reset mobile status if user edits phone after sending OTP
        if (mobileStatus === 'sent') {
            setMobileStatus('idle');
            setMobileOtp('');
        }
    };

    const validateForm = () => {
        let tempErrors = {};
        let isValid = true;

        if (!formData.email.trim()) {
            tempErrors.email = 'Please enter your email address';
            isValid = false;
        } else if (!formData.email.includes('@') || !formData.email.includes('.')) {
            tempErrors.email = 'Please enter a valid email address';
            isValid = false;
        }
        if (!formData.firstName.trim()) {
            tempErrors.firstName = 'Please enter your first name';
            isValid = false;
        }
        if (!formData.lastName.trim()) {
            tempErrors.lastName = 'Please enter your last name';
            isValid = false;
        }
        if (!formData.phoneNumber.trim()) {
            tempErrors.phoneNumber = 'Please enter your phone number';
            isValid = false;
        } else {
            const phoneRegex = /^\d{10}$/;
            if (!phoneRegex.test(formData.phoneNumber.trim())) {
                tempErrors.phoneNumber = 'Phone number must be exactly 10 digits';
                isValid = false;
            }
        }
        if (formData.password.length < 6) {
            tempErrors.password = 'Password must be at least 6 characters long';
            isValid = false;
        }
        if (!formData.confirmPassword) {
            tempErrors.confirmPassword = 'Please confirm your password';
            isValid = false;
        } else if (formData.password !== formData.confirmPassword) {
            tempErrors.confirmPassword = 'Passwords do not match';
            isValid = false;
        }
        if (!agreedToTerms) {
            tempErrors.agreedToTerms = 'You must read and agree to the Privacy Policy & Terms';
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    const sendEmailVerificationHandler = async () => {
        setError('');
        setSuccess('');
        setLoadingAction('sendEmail');
        try {
            // Always check existence first (guards against fast typing before debounce fires)
            const existsRes = await checkExistsApi({ email: formData.email });
            if (existsRes.exists) {
                setErrors(prev => ({ ...prev, email: existsRes.message || 'Email is already in use by another account.' }));
                setLoadingAction('');
                return;
            }

            // Clear any stale error only after we've confirmed the email is free
            setErrors((prev) => ({ ...prev, email: '' }));

            await sendEmailVerification({ email: formData.email });
            setEmailStatus('sent');
            setResendTimerEmail(30);
            setSuccess('Verification code sent to your email.');
        } catch (error) {
            console.error('Email verification send error:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to send email code. Please try again.';
            if (errorMessage.toLowerCase().includes('already') || errorMessage.toLowerCase().includes('in use')) {
                setErrors(prev => ({ ...prev, email: errorMessage }));
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoadingAction('');
        }
    };

    const verifyEmailOTP = async () => {
        setError('');
        setErrors(prev => ({ ...prev, emailOtp: '' }));
        setLoadingAction('verifyEmail');
        try {
            await confirmEmailVerification({ email: formData.email, otp: emailOtp });
            setSuccess('Email verified successfully!');
            setEmailStatus('verified');
        } catch (error) {
            console.error('Email OTP verification error:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Invalid Email OTP.';
            setErrors(prev => ({ ...prev, emailOtp: errorMessage }));
        } finally {
            setLoadingAction('');
        }
    };

    const sendMobileVerificationHandler = async () => {
        setError('');
        setSuccess('');
        setLoadingAction('sendMobile');
        try {
            const fullMobile = '+91' + formData.phoneNumber.trim();

            // Always check existence first (guards against fast typing before debounce fires)
            const existsRes = await checkExistsApi({ mobile: fullMobile });
            if (existsRes.exists) {
                setErrors(prev => ({ ...prev, phoneNumber: existsRes.message || 'Mobile number is already in use by another account.' }));
                setLoadingAction('');
                return;
            }

            // Clear any stale error only after we've confirmed the number is free
            setErrors((prev) => ({ ...prev, phoneNumber: '' }));

            await sendMobileVerification({ mobile: fullMobile });
            setMobileStatus('sent');
            setResendTimerMobile(30);
            setSuccess('Verification SMS sent successfully.');
        } catch (error) {
            console.error('Mobile verification send error:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Failed to send verification SMS. Please try again.';
            if (errorMessage.toLowerCase().includes('already') || errorMessage.toLowerCase().includes('in use')) {
                setErrors(prev => ({ ...prev, phoneNumber: errorMessage }));
            } else {
                setError(errorMessage);
            }
        } finally {
            setLoadingAction('');
        }
    };

    const verifyMobileOTP = async () => {
        setError('');
        setErrors(prev => ({ ...prev, mobileOtp: '' }));
        setLoadingAction('verifyMobile');
        try {
            const fullMobile = '+91' + formData.phoneNumber.trim();
            await confirmMobileVerification({ mobile: fullMobile, otp: mobileOtp });
            setSuccess('Mobile verified successfully!');
            setMobileStatus('verified');
        } catch (error) {
            console.error('Mobile OTP verification error:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Invalid Mobile OTP.';
            setErrors(prev => ({ ...prev, mobileOtp: errorMessage }));
        } finally {
            setLoadingAction('');
        }
    };

    const handleSignUp = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        if (emailStatus !== 'verified') {
            setError('Please verify your email address first.');
            return;
        }

        if (mobileStatus !== 'verified') {
            setError('Please verify your phone number first.');
            return;
        }

        // Both verified, proceed with registration
        await registerUser();
    };

    const registerUser = async () => {
        setError('');
        setLoadingAction('register');
        try {
            // Proceed with registration
            const acceptedTermsAt = new Date().toISOString();
            const fullMobile = '+91' + formData.phoneNumber.trim();

            const apiPayload = {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                mobile: fullMobile,
                password: formData.password,
                acceptedTermsAt: acceptedTermsAt,
                type: recruiterType,
                isEmailVerified: true
            };

            const selectedRegisterApi = recruiterType === 'employer' ? registerApi : freelancerRegisterApi;
            const response = await selectedRegisterApi(apiPayload);

            // Debug: Log the full response
            console.log('SignUp response:', response);

            // Store authentication data if token is returned
            const tokenValue = extractTokenFromResponse(response);
            if (tokenValue) {
                localStorage.setItem('token', tokenValue);
                localStorage.setItem('authToken', tokenValue);
                console.log('Stored token/authToken in localStorage');
            } else {
                console.warn('No token found in signup response:', response);
            }

            // Store employerId if returned - check all possible locations
            const employerIdValue = response.employerId ?? response.data?.employerId ?? response.employer?.id ?? response.data?.employer?.id;

            console.log('Extracted employerId from response:', employerIdValue);

            if (employerIdValue !== undefined && employerIdValue !== null) {
                localStorage.setItem('employerId', String(employerIdValue));
                console.log('Stored employerId in localStorage:', String(employerIdValue));
            } else {
                console.warn('No employerId found in signup response');
            }
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.removeItem('hasCompany');

            // Clear temporary signup session state
            clearSavedSignupState();

            // Show success confirmation popup dialog instead of silent navigation
            setSuccessDialogOpen(true);
        } catch (error) {
            console.error('Registration error:', error);
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'Registration failed. Please try again.';
            setError(errorMessage);
        } finally {
            setLoadingAction('');
        }
    };

    const handleChangeEmail = () => {
        setEmailStatus('idle');
        setEmailOtp('');
        setErrors(prev => ({ ...prev, email: '', emailOtp: '' }));
        setFormData(prev => ({ ...prev, email: '' }));
        setSuccess('');
    };

    const handleChangeMobile = () => {
        setMobileStatus('idle');
        setMobileOtp('');
        setErrors(prev => ({ ...prev, phoneNumber: '', mobileOtp: '' }));
        setFormData(prev => ({ ...prev, phoneNumber: '' }));
        setSuccess('');
    };

    const passwordStrength = getPasswordStrength(formData.password);

    const content = (
        <>
            {/* Left Section - SignUp Form */}
            <Box
                sx={{
                    width: onlyForm ? '100%' : { xs: '100%', md: '50%' },
                    height: { md: '100vh' },
                    minHeight: '100vh',
                    backgroundColor: '#FFFFFF',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 3, sm: 5, md: 6 },
                    overflowY: { md: 'auto' },
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none',
                }}
            >
                <Paper elevation={0} sx={{ width: '100%', maxWidth: 450, padding: { xs: 3, md: 4 }, borderRadius: '16px', }}
                >
                    {/* Step progress bar */}
                    {/* Step progress bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#5F27CD', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600 }}>
                                1
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#5F27CD' }}>Basic Details</Typography>
                        </Box>
                        <Box sx={{ flexGrow: 1, height: '2px', backgroundColor: '#e5e7eb', mx: 2 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 24, height: 24, borderRadius: '50%', backgroundColor: '#e5e7eb', color: '#9ca3af', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600 }}>
                                2
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#9ca3af' }}>Company Setup</Typography>
                        </Box>
                    </Box>

                    {/* Create an employer account Title */}
                    <Typography variant="h4" sx={{ fontWeight: 600, color: '#000', mb: 1 }}>
                        Create an employer account
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, fontWeight: 500 }}>
                        Step 1 of 2 • Basic Details
                    </Typography>

                    {/* Error Message */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}> {error}  </Alert>
                    )}

                    {/* Success Message */}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }}> {success}  </Alert>
                    )}

                    {/* Recruiter Type Radio Buttons */}
                    <FormControl component="fieldset" sx={{ mb: 3 }}>
                        <RadioGroup row value={recruiterType} onChange={(e) => setRecruiterType(e.target.value)}
                            sx={{
                                flexDirection: { xs: 'column', sm: 'row' },
                                '& .MuiFormControlLabel-root': {
                                    marginRight: { xs: 0, sm: 3 },
                                    mb: { xs: 1, sm: 0 },
                                },
                            }}
                        >
                            <FormControlLabel value="employer" control={
                                <Radio sx={{ color: '#6B31C4', '&.Mui-checked': { color: '#6B31C4', }, }} />
                            }
                                label="Employer"
                            />
                            <FormControlLabel
                                value="freelancer"
                                control={
                                    <Radio
                                        sx={{
                                            color: '#6B31C4',
                                            '&.Mui-checked': {
                                                color: '#6B31C4',
                                            },
                                        }}
                                    />
                                }
                                label="Freelancer"
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Mail ID Field */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <EmailIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                            <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                Mail ID  <span style={{ color: '#991B1B' }}>*</span>
                            </Typography>
                            {/* Verified Badge + Change Button */}
                            {emailStatus === 'verified' && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#10b981', color: 'white', px: 1.5, py: 0.25, borderRadius: '12px', fontSize: '12px', fontWeight: 500 }}>
                                        <CheckCircleIcon sx={{ fontSize: 14 }} /> Email Verified
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={handleChangeEmail}
                                        sx={{ fontSize: '12px', color: '#6B31C4', textTransform: 'none', ml: 0.5, p: '2px 6px', minWidth: 'auto', '&:hover': { backgroundColor: '#f3f0ff' } }}
                                    >
                                        Change
                                    </Button>
                                </>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <TextField
                                fullWidth size="small"
                                placeholder="Enter your mail id"
                                value={formData.email}
                                onChange={handleInputChange('email')}
                                disabled={loadingAction !== '' || emailStatus === 'verified'}
                                type="email"
                                autoComplete="off"
                                error={!!errors.email}
                                helperText={errors.email}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                            />
                            {emailStatus === 'idle' && (
                                <Button
                                    variant="outlined"
                                    onClick={sendEmailVerificationHandler}
                                    disabled={loadingAction !== '' || !formData.email || !formData.email.includes('@') || !formData.email.includes('.') || !!errors.email}
                                    sx={{
                                        height: '40px', minWidth: '120px', borderColor: '#6B31C4', color: '#6B31C4',
                                        '&:hover': { borderColor: '#6B31C4', backgroundColor: '#f3f0ff' },
                                        '&:disabled': { borderColor: '#d1d5db', color: '#d1d5db' },
                                    }}
                                >
                                    {loadingAction === 'sendEmail' ? <CircularProgress size={16} sx={{ color: '#6B31C4' }} /> : 'Verify Email'}
                                </Button>
                            )}
                        </Box>
                        
                        {/* Email OTP Field */}
                        {emailStatus === 'sent' && (
                            <Box sx={{ mt: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
                                <Typography variant="body2" sx={{ mb: 1, color: '#4b5563' }}>
                                    Enter the 4-digit code sent to your email.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth size="small" placeholder="4-digit OTP"
                                        value={emailOtp}
                                        onChange={(e) => {
                                            setEmailOtp(e.target.value.replace(/\D/g, '').slice(0, 4));
                                            setErrors(prev => ({ ...prev, emailOtp: '' }));
                                        }}
                                        disabled={loadingAction !== ''}
                                        error={!!errors.emailOtp}
                                        helperText={errors.emailOtp}
                                        sx={{ backgroundColor: 'white' }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={verifyEmailOTP}
                                        disabled={loadingAction !== '' || emailOtp.length !== 4}
                                        sx={{ minWidth: '100px', backgroundColor: '#6B31C4', color: 'white', '&:hover': { backgroundColor: '#5a27a8' }, '&:disabled': { backgroundColor: '#9ca3af' } }}
                                    >
                                        {loadingAction === 'verifyEmail' ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Verify'}
                                    </Button>
                                </Box>
                                <Button
                                    variant="text"
                                    onClick={sendEmailVerificationHandler}
                                    disabled={loadingAction !== '' || resendTimerEmail > 0}
                                    sx={{ mt: 1, color: '#6B31C4', fontSize: '12px', textTransform: 'none', '&:disabled': { color: '#d1d5db' } }}
                                >
                                    {resendTimerEmail > 0 ? `Resend code in ${resendTimerEmail}s` : 'Resend Code'}
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {/* First Name and Last Name Fields */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
                        <Box sx={{ flex: 1, width: { xs: '100%', md: 'auto' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PersonIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                    First Name <span style={{ color: '#991B1B' }}>*</span>
                                </Typography>
                            </Box>
                            <TextField
                                fullWidth size="small" placeholder="Enter First Name"
                                value={formData.firstName} onChange={handleInputChange('firstName')}
                                disabled={loadingAction !== ''}
                                error={!!errors.firstName}
                                helperText={errors.firstName}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ color: '#000', fontWeight: 500, mb: 1 }}>
                                Last Name <span style={{ color: '#991B1B' }}>*</span>
                            </Typography>
                            <TextField
                                fullWidth size="small" placeholder="Enter Last Name"
                                value={formData.lastName} onChange={handleInputChange('lastName')}
                                disabled={loadingAction !== ''}
                                error={!!errors.lastName}
                                helperText={errors.lastName}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                            />
                        </Box>
                    </Box>

                    {/* Phone Number Field */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <PhoneIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                            <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                Phone Number <span style={{ color: '#991B1B' }}>*</span>
                            </Typography>
                            {mobileStatus === 'verified' && (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#10b981', color: 'white', px: 1.5, py: 0.25, borderRadius: '12px', fontSize: '12px', fontWeight: 500 }}>
                                        <CheckCircleIcon sx={{ fontSize: 14 }} /> Mobile Verified
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={handleChangeMobile}
                                        sx={{ fontSize: '12px', color: '#6B31C4', textTransform: 'none', ml: 0.5, p: '2px 6px', minWidth: 'auto', '&:hover': { backgroundColor: '#f3f0ff' } }}
                                    >
                                        Change
                                    </Button>
                                </>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <TextField
                                fullWidth size="small"
                                placeholder="Enter Phone Number"
                                value={formData.phoneNumber}
                                onChange={handlePhoneNumberChange}
                                disabled={loadingAction !== '' || mobileStatus === 'verified'}
                                type="tel"
                                inputProps={{ maxLength: 10, pattern: '[0-9]{10}' }}
                                error={!!errors.phoneNumber}
                                helperText={errors.phoneNumber}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">+91</InputAdornment>
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                            />
                            {mobileStatus === 'idle' && (
                                <Button
                                    variant="outlined"
                                    onClick={sendMobileVerificationHandler}
                                    disabled={loadingAction !== '' || !formData.phoneNumber || formData.phoneNumber.length < 10 || !!errors.phoneNumber}
                                    sx={{
                                        height: '40px', minWidth: '120px', borderColor: '#6B31C4', color: '#6B31C4',
                                        '&:hover': { borderColor: '#6B31C4', backgroundColor: '#f3f0ff' },
                                        '&:disabled': { borderColor: '#d1d5db', color: '#d1d5db' },
                                    }}
                                >
                                    {loadingAction === 'sendMobile' ? <CircularProgress size={16} sx={{ color: '#6B31C4' }} /> : 'Send OTP'}
                                </Button>
                            )}
                        </Box>
                        
                        {/* Mobile OTP Field */}
                        {mobileStatus === 'sent' && (
                            <Box sx={{ mt: 2, p: 2, border: '1px solid #e5e7eb', borderRadius: '4px', backgroundColor: '#f9fafb' }}>
                                <Typography variant="body2" sx={{ mb: 1, color: '#4b5563' }}>
                                    Enter the 4-digit code sent to your mobile.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <TextField
                                        fullWidth size="small" placeholder="4-digit OTP"
                                        value={mobileOtp}
                                        onChange={(e) => {
                                            setMobileOtp(e.target.value.replace(/\D/g, '').slice(0, 4));
                                            setErrors(prev => ({ ...prev, mobileOtp: '' }));
                                        }}
                                        disabled={loadingAction !== ''}
                                        error={!!errors.mobileOtp}
                                        helperText={errors.mobileOtp}
                                        sx={{ backgroundColor: 'white' }}
                                    />
                                    <Button
                                        variant="contained"
                                        onClick={verifyMobileOTP}
                                        disabled={loadingAction !== '' || mobileOtp.length !== 4}
                                        sx={{ minWidth: '100px', backgroundColor: '#6B31C4', color: 'white', '&:hover': { backgroundColor: '#5a27a8' }, '&:disabled': { backgroundColor: '#9ca3af' } }}
                                    >
                                        {loadingAction === 'verifyMobile' ? <CircularProgress size={16} sx={{ color: 'white' }} /> : 'Verify'}
                                    </Button>
                                </Box>
                                <Button
                                    variant="text"
                                    onClick={sendMobileVerificationHandler}
                                    disabled={loadingAction !== '' || resendTimerMobile > 0}
                                    sx={{ mt: 1, color: '#6B31C4', fontSize: '12px', textTransform: 'none', '&:disabled': { color: '#d1d5db' } }}
                                >
                                    {resendTimerMobile > 0 ? `Resend code in ${resendTimerMobile}s` : 'Resend Code'}
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {/* Create Password Field */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LockIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                            <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                Create Password <span style={{ color: '#991B1B' }}>*</span>
                            </Typography>
                        </Box>
                        <TextField
                            fullWidth size="small"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Enter Password"
                            value={formData.password}
                            onChange={handleInputChange('password')}
                            disabled={loadingAction !== ''}
                            autoComplete="new-password"
                            error={!!errors.password}
                            helperText={errors.password}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                        />
                        {formData.password && (
                            <Box sx={{ mt: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                                        Password Strength:
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: passwordStrength.color, fontWeight: 700, textTransform: 'uppercase' }}>
                                        {passwordStrength.label}
                                    </Typography>
                                </Box>
                                <Box sx={{ width: '100%', height: 4, backgroundColor: '#E5E7EB', borderRadius: 1, overflow: 'hidden' }}>
                                    <Box
                                        sx={{
                                            width: `${passwordStrength.percent}%`,
                                            height: '100%',
                                            backgroundColor: passwordStrength.color,
                                            transition: 'width 0.3s ease-in-out, background-color 0.3s ease-in-out'
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>

                    {/* Confirm Password Field */}
                    <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <LockIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                            <Typography variant="body1" sx={{ color: '#000', fontWeight: 500 }}>
                                Confirm Password <span style={{ color: '#991B1B' }}>*</span>
                            </Typography>
                        </Box>
                        <TextField
                            fullWidth size="small"
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm Password"
                            value={formData.confirmPassword}
                            onChange={handleInputChange('confirmPassword')}
                            disabled={loadingAction !== ''}
                            autoComplete="new-password"
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px' } }}
                        />
                    </Box>

                    {/* Privacy Policy Checkbox */}
                    <Box sx={{ mb: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={agreedToTerms}
                                    onChange={(e) => {
                                        setAgreedToTerms(e.target.checked);
                                        setError('');
                                        setErrors(prev => ({ ...prev, agreedToTerms: '' }));
                                    }}
                                    disabled={loadingAction !== ''}
                                    sx={{ color: '#6B31C4', '&.Mui-checked': { color: '#6B31C4' } }}
                                />
                            }
                            label={
                                <Typography variant="body1" sx={{ color: '#000', fontSize: '14px' }}>
                                    I have read and understood{' '}
                                    <Link href="/privacy-policy" target="_blank" rel="noopener noreferrer" sx={{ color: '#6B31C4', textDecoration: 'none', fontWeight: 500 }}>
                                        Privacy Policy
                                    </Link>{' '}
                                    and{' '}
                                    <Link href="/terms-conditions" target="_blank" rel="noopener noreferrer" sx={{ color: '#6B31C4', textDecoration: 'none', fontWeight: 500 }}>
                                        Terms & Conditions
                                    </Link>
                                </Typography>
                            }
                        />
                        {errors.agreedToTerms && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4 }}>
                                {errors.agreedToTerms}
                            </Typography>
                        )}
                    </Box>

                    {/* Sign Up Button */}
                    <Button disableElevation disableFocusRipple
                        disableRipple
                        disableTouchRipple
                        fullWidth
                        variant="contained"
                        onClick={handleSignUp}
                        disabled={loadingAction !== '' || emailStatus !== 'verified' || mobileStatus !== 'verified'}
                        sx={{
                            backgroundColor: '#6B31C4',
                            color: 'white',
                            py: 1.4,
                            mb: 3,
                            borderRadius: '4px',
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '16px',
                            '&:hover': {
                                backgroundColor: '#5a27a8',
                            },
                            '&:disabled': {
                                backgroundColor: '#d1d5db',
                                color: '#9ca3af',
                            },
                        }}
                    >
                        {loadingAction === 'register' ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={20} sx={{ color: 'white' }} />
                                <span>Creating Account...</span>
                            </Box>
                        ) : (
                            'Create Recruiter Account'
                        )}
                    </Button>

                    {/* Divider with Or */}
                    <Box sx={{ position: 'relative', mb: 3 }}>
                        <Divider />
                        <Typography
                            variant="body1"
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                backgroundColor: 'white',
                                px: 2,
                                color: '#666',
                            }}
                        >
                            Or
                        </Typography>
                    </Box>

                    {/* Login Link */}
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#000',
                                fontSize: '14px',
                            }}
                        >
                            Already have an Account?{' '}
                            <Link
                                component="button"
                                onClick={() => navigate('/login')}
                                sx={{
                                    color: '#6B31C4',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                Login Here
                            </Link>
                        </Typography>
                    </Box>

                </Paper>

                {/* Trust Indicators */}
                <Box 
                    sx={{ 
                        display: 'flex', 
                        flexDirection: 'row',
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        gap: { xs: 1.5, sm: 2 }, 
                        mt: 4, 
                        width: '100%',
                        maxWidth: 450,
                        px: 1,
                        boxSizing: 'border-box'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                        <ShieldIcon sx={{ color: '#10B981', fontSize: 16 }} />
                        <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600, fontSize: '10.5px', letterSpacing: '0.1px' }}>
                            Secure Registration
                        </Typography>
                    </Box>
                    <Box sx={{ width: '1px', height: 12, backgroundColor: '#cbd5e1' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                        <HttpsIcon sx={{ color: '#10B981', fontSize: 16 }} />
                        <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600, fontSize: '10.5px', letterSpacing: '0.1px' }}>
                            Encrypted Data
                        </Typography>
                    </Box>
                    <Box sx={{ width: '1px', height: 12, backgroundColor: '#cbd5e1' }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                        <VerifiedIcon sx={{ color: '#10B981', fontSize: 16 }} />
                        <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 600, fontSize: '10.5px', letterSpacing: '0.1px' }}>
                            Trusted by Employers
                        </Typography>
                    </Box>
                </Box>
            </Box>
            {/* Success Dialog Modal popup */}
            <Dialog
                open={successDialogOpen}
                onClose={() => {}}
                aria-labelledby="success-dialog-title"
                aria-describedby="success-dialog-description"
                PaperProps={{
                    sx: { borderRadius: '12px', p: 1 }
                }}
            >
                <DialogTitle id="success-dialog-title" sx={{ fontWeight: 600, color: '#10b981' }}>
                    Registration Successful!
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="success-dialog-description" sx={{ color: '#374151', mt: 1 }}>
                        Your recruiter account has been created successfully. Let's set up your company details to start posting jobs.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => {
                            setSuccessDialogOpen(false);
                            navigate('/company-details');
                        }}
                        variant="contained"
                        sx={{ backgroundColor: '#6B31C4', color: 'white', px: 4, py: 1.2, fontWeight: 600, textTransform: 'none', '&:hover': { backgroundColor: '#5a27a8' } }}
                        autoFocus
                    >
                        Continue to Company Setup
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );

    if (onlyForm) {
        return content;
    }

    return (
        <Box
            sx={{
                height: { xs: 'auto', md: '100vh' },
                minHeight: '100vh',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                overflow: 'hidden'
            }}
        >
            {/* ── keyframes ── */}
            <style>{`
                * { font-family: 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif; }

                @keyframes blink    { 0%,100%{opacity:1} 50%{opacity:0} }
                @keyframes scanLine { 0%{top:0%;opacity:1} 100%{top:100%;opacity:0} }
                @keyframes fadeUp   { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
                @keyframes tickPop  { 0%{transform:scale(0) rotate(-15deg);opacity:0} 65%{transform:scale(1.2) rotate(3deg);opacity:1} 100%{transform:scale(1) rotate(0)} }
                @keyframes glowPulse{ 0%,100%{opacity:.35;transform:scale(1)} 50%{opacity:.6;transform:scale(1.04)} }
                @keyframes shimmer  { 0%{left:-60%} 100%{left:120%} }
                @keyframes floatFile{ 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }
                @keyframes ringPulse{ 0%{box-shadow:0 0 0 0 rgba(167,139,250,.55)} 100%{box-shadow:0 0 0 7px rgba(167,139,250,0)} }
            `}</style>

            {content}

            {/* ══════════════════════════════════════════════════
                RIGHT PANEL  (dark, animated)
            ══════════════════════════════════════════════════ */}
            <Box sx={{
                width: { xs: '100%', md: '50%' },
                height: { md: '100vh' },
                maxHeight: { md: '100vh' },
                minHeight: { md: '100vh' },
                boxSizing: 'border-box',
                /* dark purple gradient */
                background: 'linear-gradient(160deg, #070514 0%, #110E2A 55%, #2A1868 100%)',
                display: { xs: 'none', md: 'flex' },
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'center',        /* horizontally center children */
                p: { md: '40px 30px', lg: '50px 40px' },
                position: 'relative',
                overflow: 'hidden',
                color: '#fff',
                borderTopLeftRadius: { md: '30px' },
                borderBottomLeftRadius: { md: '30px' },
            }}>
                {/* glow blobs */}
                <Box sx={{
                    position: 'absolute', bottom: -80, right: -80, width: 400, height: 400, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(109,40,217,.22) 0%, transparent 70%)',
                    animation: 'glowPulse 4s ease-in-out infinite', pointerEvents: 'none'
                }} />
                <Box sx={{
                    position: 'absolute', top: -50, left: -50, width: 250, height: 250, borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(96,165,250,.09) 0%, transparent 70%)', pointerEvents: 'none'
                }} />

                {/* ── TOP: logo ── */}
                <Box sx={{ width: '92%', maxWidth: '520px', display: 'flex', alignItems: 'center', gap: 1.5, zIndex: 1 }}>
                    <Box component="img" src={logoImage} alt="RONO Jobs"
                        sx={{ height: 28, objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
                    <Chip
                        icon={<AiIcon sx={{ fontSize: '12px !important', color: '#C084FC !important' }} />}
                        label="AI RECRUITER" size="small"
                        sx={{
                            backgroundColor: 'rgba(192,132,252,.12)', color: '#DDD6FE', fontWeight: 700,
                            fontSize: '10px', letterSpacing: '.7px', height: 20,
                            border: '1px solid rgba(192,132,252,.25)', borderRadius: '5px'
                        }}
                    />
                </Box>

                {/* ── MIDDLE: heading + stepper + carousel ── */}
                <Box sx={{ width: '92%', maxWidth: '520px', zIndex: 1, mt: -6 }}>

                    <Typography sx={{
                        fontWeight: 800, color: '#F8FAFC', mb: 1.4, lineHeight: 1.25,
                        fontSize: { md: '30px', lg: '34px' }, letterSpacing: '-.6px'
                    }}>
                        Find Top Talent with AI,<br />
                        <span style={{ color: '#A78BFA' }}>In Seconds — Not Weeks</span>
                    </Typography>
                    <Typography sx={{ color: '#64748B', mb: 3.5, fontSize: '16.5px', lineHeight: 1.7 }}>
                        Upload a JD and our AI parses it, scans your talent pool, and surfaces the best matches instantly.
                    </Typography>

                    {/* ── Stepper ── */}
                    <Box sx={{ width: '100%', mb: 2, position: 'relative' }}>
                        {/* track */}
                        <Box sx={{
                            position: 'absolute', top: 15,
                            left: 'calc(12.5% + 15px)', right: 'calc(12.5% + 15px)',
                            height: 2, backgroundColor: 'rgba(255,255,255,.1)', zIndex: 0
                        }} />
                        {/* fill */}
                        <Box sx={{
                            position: 'absolute', top: 15, left: 'calc(12.5% + 15px)',
                            width: activeStep === 0 ? '0%' : `${Math.round((activeStep / 3) * 75)}%`,
                            height: 2, background: 'linear-gradient(90deg, #7C3AED, #A78BFA)',
                            transition: 'width .7s cubic-bezier(.4,0,.2,1)', zIndex: 0
                        }} />
                        {/* circles */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                            {STEPS.map(s => {
                                const done = s.id <= activeStep;
                                const current = s.id === activeStep;
                                return (
                                    <Box key={s.id} onClick={() => setActiveStep(s.id)}
                                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer', flex: 1 }}>
                                        <Box sx={{
                                            width: 30, height: 30, borderRadius: '50%', mb: .5,
                                            /* solid bg — line NEVER bleeds through */
                                            backgroundColor: done ? '#7C3AED' : '#0D0B1F',
                                            border: current ? `2px solid ${s.color}` : done ? '2px solid #7C3AED' : '2px solid rgba(255,255,255,.14)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            transition: 'all .4s cubic-bezier(.4,0,.2,1)',
                                            animation: current ? 'ringPulse 1.4s ease-out infinite' : 'none',
                                            boxShadow: done && !current ? '0 2px 10px rgba(124,58,237,.45)' : 'none',
                                            zIndex: 2, position: 'relative'
                                        }}>
                                            {done
                                                ? <CheckCircleIcon sx={{ color: '#fff', fontSize: 15, animation: 'tickPop .35s ease' }} />
                                                : <Typography sx={{ color: 'rgba(255,255,255,.4)', fontSize: '10px', fontWeight: 700 }}>{s.id + 1}</Typography>
                                            }
                                        </Box>
                                        <Typography sx={{
                                            fontSize: '10.5px', textAlign: 'center', lineHeight: 1.3,
                                            fontWeight: current ? 700 : 400,
                                            color: done ? '#CBD5E1' : 'rgba(255,255,255,.28)',
                                            transition: 'color .35s ease'
                                        }}>
                                            {s.label}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* ── Carousel card ── */}
                    <Box sx={{
                        width: '100%', borderRadius: '14px',
                        border: '1px solid rgba(255,255,255,.09)',
                        backgroundColor: 'rgba(255,255,255,.035)',
                        backdropFilter: 'blur(12px)',
                        overflow: 'hidden',
                        boxShadow: '0 4px 28px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.07)'
                    }}>
                        {/* track */}
                        <Box sx={{
                            display: 'flex', width: '400%',
                            transform: `translateX(-${activeStep * 25}%)`,
                            transition: 'transform .65s cubic-bezier(.4,0,.2,1)'
                        }}>
                            {[
                                <SlideUpload active={activeStep === 0} />,
                                <SlideAnalyze active={activeStep === 1} />,
                                <SlideScan active={activeStep === 2} />,
                                <SlideResults active={activeStep === 3} />,
                            ].map((slide, i) => (
                                <Box key={i} sx={{ width: '25%', p: 2.5, boxSizing: 'border-box', flexShrink: 0 }}>
                                    {slide}
                                </Box>
                            ))}
                        </Box>
                        {/* dots */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: .8, py: 1.2, borderTop: '1px solid rgba(255,255,255,.06)' }}>
                            {STEPS.map(s => (
                                <Box key={s.id} onClick={() => setActiveStep(s.id)} sx={{
                                    width: activeStep === s.id ? 20 : 6, height: 6, borderRadius: 3,
                                    backgroundColor: activeStep === s.id ? '#7C3AED' : 'rgba(255,255,255,.18)',
                                    cursor: 'pointer', transition: 'all .35s ease'
                                }} />
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* ── BOTTOM: footer ── */}
                <Box sx={{ width: '92%', maxWidth: '520px', zIndex: 1 }}>
                    <Typography sx={{ fontSize: '10.5px', color: 'rgba(255,255,255,.2)' }}>
                        © {new Date().getFullYear()} RONO Jobs™ · Powered by AI
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default SignUp;
